import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getUserFeatures,
  type UserFeatures,
} from "../../services/featureService";

type FeaturePlatform = "web" | "mobile";

interface UserFeaturesQueryOptions {
  enabled?: boolean;
}

export type FeatureAccessMap = Record<string, boolean>;

const DEFAULT_USER_FEATURES: UserFeatures = {
  platform: [],
  businesses: [],
};

const DEFAULT_FEATURE_ACCESS: { platform: FeatureAccessMap } = {
  platform: {},
};

const FEATURE_ACCESS_CACHE = 1000 * 60 * 5; // 5 minutes

const toFeatureAccessMap = (featureKeys: string[]): FeatureAccessMap => {
  return Object.fromEntries(
    featureKeys.map((featureKey) => [featureKey, true])
  );
};

const selectFeatureAccessByCategory = (
  userFeatures: UserFeatures,
  category: string
): FeatureAccessMap => {
  if (category !== "platform") {
    return {};
  }

  return toFeatureAccessMap(userFeatures.platform);
};

const selectAllFeatureAccess = (userFeatures: UserFeatures) => {
  return {
    platform: toFeatureAccessMap(userFeatures.platform),
  };
};

const selectFeatureEnabled = (
  userFeatures: UserFeatures,
  category: string,
  key: string
): boolean => {
  const categoryAccess = selectFeatureAccessByCategory(userFeatures, category);
  return !!categoryAccess[key];
};

/**
 * Raw API hook for GET /api/v2/user/me/features
 */
export const useUserFeatures = (
  platform: FeaturePlatform = "web",
  options?: UserFeaturesQueryOptions
) => {
  return useQuery({
    queryKey: ["feature-access", "raw", platform],
    queryFn: async () => {
      try {
        const data = await getUserFeatures(platform);
        return data || DEFAULT_USER_FEATURES;
      } catch (error) {
        console.error("Error fetching raw user features:", error);
        return DEFAULT_USER_FEATURES;
      }
    },
    enabled: options?.enabled ?? true,
    staleTime: FEATURE_ACCESS_CACHE,
    gcTime: FEATURE_ACCESS_CACHE + 1000 * 60 * 2,
  });
};

/**
 * Hook to get all accessible features derived from the raw user-features payload
 */
export const useAllFeatureAccess = () => {
  const query = useUserFeatures("web");

  return useMemo(
    () => ({
      ...query,
      data: query.data
        ? selectAllFeatureAccess(query.data)
        : DEFAULT_FEATURE_ACCESS,
    }),
    [query]
  );
};

/**
 * Hook to get a single feature-access state by category and key
 */
export const useFeatureAccessByKey = (category: string, key: string) => {
  const query = useUserFeatures("web", { enabled: !!category && !!key });

  return useMemo(
    () => ({
      ...query,
      data:
        category && key
          ? selectFeatureEnabled(
              query.data || DEFAULT_USER_FEATURES,
              category,
              key
            )
          : false,
      fetchStatus: category && key ? query.fetchStatus : "idle",
      isFetching: category && key ? query.isFetching : false,
      isLoading: category && key ? query.isLoading : false,
      isPending: category && key ? query.isPending : false,
      isRefetching: category && key ? query.isRefetching : false,
    }),
    [category, key, query]
  );
};

/**
 * Hook to get accessible features by category
 */
export const useFeatureAccessByCategory = (category: string) => {
  const query = useUserFeatures("web", { enabled: !!category });

  return useMemo(
    () => ({
      ...query,
      data: category
        ? selectFeatureAccessByCategory(
            query.data || DEFAULT_USER_FEATURES,
            category
          )
        : {},
      fetchStatus: category ? query.fetchStatus : "idle",
      isFetching: category ? query.isFetching : false,
      isLoading: category ? query.isLoading : false,
      isPending: category ? query.isPending : false,
      isRefetching: category ? query.isRefetching : false,
    }),
    [category, query]
  );
};
